import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NavBarHome from '../NavBarHome/NavBarHome';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LabelList
} from 'recharts';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { 
  logoutUsuario, 
  fetchUsuarios, 
  getTodosClientes,
  obtenerHorasReporteUsuario,
  obtenerHorasReporteTodosUsuarios,
  limpiarReporteUsuario,
  obtenerHorasReporteUsuarioConCliente,
  obtenerHorasReporteTodosUsuariosConCliente
} from '../../actions/index.js';
import './ReporteUsuario.css';

// Paleta de colores para los gráficos (verde corporativo + variantes)
const CHART_COLORS = ['#28C76F', '#3FE07F', '#1E9E75', '#6CECA0', '#8A988F'];

const ReporteUsuario = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Selectors de Redux
  const user = useSelector(state => state.usuario);
  const usuarios = useSelector(state => state.usuarios);
  const clientes = useSelector(state => state.clientesReporte);
  const reporteUsuario = useSelector(state => state.reporteUsuario);
  const { loading, error } = reporteUsuario;
  
  // Estados locales para filtros
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [mostrarFiltroCliente, setMostrarFiltroCliente] = useState(false);
  const [horasConsultadas, setHorasConsultadas] = useState([]);

  // useEffect para cargar datos iniciales
  useEffect(() => {
    dispatch(fetchUsuarios());
    dispatch(getTodosClientes());
  }, [dispatch]);

  // Función para manejar el cierre de sesión
  const handleLogout = () => {
    dispatch(logoutUsuario());
    history.push('/login');
  };

  // Función para formatear fecha para mostrar
  const formatearFecha = (fechaString) => {
    if (!fechaString) return 'Fecha no disponible';
    
    try {
      // Si la fecha viene en formato dd/mm/yyyy
      if (fechaString.includes('/')) {
        const [dia, mes, año] = fechaString.split('/');
        const fecha = new Date(año, mes - 1, dia);
        if (isNaN(fecha.getTime())) {
          return fechaString; // Retornar la fecha original si no se puede parsear
        }
        return fecha.toLocaleDateString('es-ES');
      }
      
      // Si viene en formato ISO
      const fecha = new Date(fechaString);
      if (isNaN(fecha.getTime())) {
        return fechaString; // Retornar la fecha original si no se puede parsear
      }
      return fecha.toLocaleDateString('es-ES');
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return fechaString; // Retornar la fecha original en caso de error
    }
  };

  // Función para formatear horas
  const formatearHoras = (horas) => {
    return `${horas}h`;
  };

  // Función para convertir cadena a buffer de bytes (necesaria para Excel)
  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
    return buf;
  };

  // Función para exportar datos a Excel
  const exportarAExcel = () => {
    if (!horasConsultadas || horasConsultadas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const datosParaExcel = horasConsultadas.map(hora => ({
      'Usuario': hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'N/A',
      'Cliente': hora.cliente ? hora.cliente.nombre : 'Sin cliente',
      'Servicio': hora.servicio ? hora.servicio.nombre : 'Sin servicio',
      'Tarea': hora.tarea ? hora.tarea.nombre : 'Sin tarea',
      'Descripción': hora.descripcion || 'Sin descripción',
      'Horas': hora.horas,
      'Fecha': formatearFecha(hora.fechaCarga)
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(datosParaExcel);
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte Horas Usuario');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
    saveAs(blob, `reporte_horas_usuario_${new Date().getTime()}.xlsx`);
  };

  // Función para exportar resumen de usuarios a Excel
  const exportarResumenUsuariosAExcel = () => {
    const resumen = obtenerResumenUsuarios();
    
    if (resumen.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(resumen);
    XLSX.utils.book_append_sheet(wb, ws, 'Resumen por Usuario');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
    saveAs(blob, `resumen_usuarios_${new Date().getTime()}.xlsx`);
  };

  // Función para exportar resumen de clientes a Excel
  const exportarResumenClientesAExcel = () => {
    const resumen = obtenerResumenClientes();
    
    if (resumen.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(resumen);
    XLSX.utils.book_append_sheet(wb, ws, 'Resumen por Cliente');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
    saveAs(blob, `resumen_clientes_${new Date().getTime()}.xlsx`);
  };

  // Función para obtener resumen de usuarios
  const obtenerResumenUsuarios = () => {
    const resumenMap = {};
    
    horasConsultadas.forEach(hora => {
      const usuarioKey = hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido';
      const seniority = hora.senorityhora || 'N/A';
      
      if (!resumenMap[usuarioKey]) {
        resumenMap[usuarioKey] = {
          'Usuario': usuarioKey,
          'Seniority': seniority,
          'Total Horas': 0,
          'Registros': 0
        };
      }
      
      resumenMap[usuarioKey]['Total Horas'] += parseFloat(hora.horas);
      resumenMap[usuarioKey]['Registros'] += 1;
    });
    
    return Object.values(resumenMap);
  };

  // Función para obtener resumen por cliente
  const obtenerResumenClientes = () => {
    const resumenMap = {};
    const totalHoras = horasConsultadas.reduce((total, hora) => total + parseFloat(hora.horas), 0);
    
    horasConsultadas.forEach(hora => {
      const clienteKey = hora.cliente ? hora.cliente.nombre : 'Sin cliente asignado';
      const usuarioKey = hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido';
      const seniority = hora.senorityhora || 'N/A';
      
      // Determinar cómo agrupar según los filtros activos
      let entryKey;
      if (clienteSeleccionado) {
        // Caso 1: Cliente seleccionado - mostrar usuarios que trabajaron para ese cliente
        entryKey = usuarioKey;
      } else if (usuarioSeleccionado) {
        // Caso 2: Usuario seleccionado (sin cliente) - mostrar clientes para ese usuario
        entryKey = `${clienteKey}_${usuarioKey}`;
      } else {
        // Caso 3: Sin filtros - agrupar solo por cliente (total por cliente)
        entryKey = clienteKey;
      }
      
      if (!resumenMap[entryKey]) {
        resumenMap[entryKey] = {
          'Cliente': clienteSeleccionado ? clienteSeleccionado.label : clienteKey,
          'Usuario': (clienteSeleccionado || usuarioSeleccionado) ? usuarioKey : undefined,
          'Seniority': seniority,
          'Total Horas': 0,
          'Registros': 0,
          'Porcentaje': 0,
          'Seniorities': {}
        };
      }
      
      resumenMap[entryKey]['Total Horas'] += parseFloat(hora.horas);
      resumenMap[entryKey]['Registros'] += 1;
      
      // Manejar seniority según el caso
      if (clienteSeleccionado || usuarioSeleccionado) {
        // Para casos específicos, mantener el seniority del usuario
        resumenMap[entryKey]['Seniority'] = seniority;
      } else {
        // Para resumen general, calcular seniority predominante por cliente
        if (!resumenMap[entryKey]['Seniorities'][seniority]) {
          resumenMap[entryKey]['Seniorities'][seniority] = 0;
        }
        resumenMap[entryKey]['Seniorities'][seniority] += parseFloat(hora.horas);
      }
    });
    
    // Calcular porcentajes y seniority predominante
    Object.values(resumenMap).forEach(entrada => {
      entrada['Porcentaje'] = totalHoras > 0 ? 
        ((entrada['Total Horas'] / totalHoras) * 100).toFixed(1) + '%' : 
        '0%';
        
      // Calcular seniority predominante para resumen general
      if (!clienteSeleccionado && !usuarioSeleccionado) {
        const seniorities = entrada['Seniorities'];
        if (Object.keys(seniorities).length > 0) {
          const seniorityPredominante = Object.keys(seniorities).reduce((a, b) => 
            seniorities[a] > seniorities[b] ? a : b
          );
          entrada['Seniority'] = seniorityPredominante;
        }
      }
      
      // Eliminar la propiedad auxiliar
      delete entrada['Seniorities'];
    });
    
    // Ordenar por total de horas descendente
    return Object.values(resumenMap).sort((a, b) => b['Total Horas'] - a['Total Horas']);
  };

  // Función para buscar horas
  const buscarHoras = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Debe seleccionar un rango de fechas');
      return;
    }

    // Convertir fechas a formato YYYY-MM-DD para el backend
    const fechaInicioStr = fechaInicio.toISOString().split('T')[0];
    const fechaFinStr = fechaFin.toISOString().split('T')[0];

    try {
      let horasData = [];
      
      if (usuarioSeleccionado) {
        // Buscar horas de un usuario específico con filtro opcional de cliente
        console.log('🔍 Buscando horas del usuario:', usuarioSeleccionado.value, 'fechas:', fechaInicioStr, 'a', fechaFinStr, 'cliente:', clienteSeleccionado?.value || 'todos');
        
        if (clienteSeleccionado) {
          // Usar endpoint que filtra por cliente en el backend
          horasData = await dispatch(obtenerHorasReporteUsuarioConCliente(
            usuarioSeleccionado.value, 
            fechaInicioStr, 
            fechaFinStr, 
            clienteSeleccionado.value
          ));
        } else {
          // Usar endpoint sin filtro de cliente
          horasData = await dispatch(obtenerHorasReporteUsuario(usuarioSeleccionado.value, fechaInicioStr, fechaFinStr));
        }
      } else {
        // Buscar horas de todos los usuarios con filtro opcional de cliente
        console.log('🔍 Buscando horas de todos los usuarios, fechas:', fechaInicioStr, 'a', fechaFinStr, 'cliente:', clienteSeleccionado?.value || 'todos');
        
        if (clienteSeleccionado) {
          // Usar endpoint que filtra por cliente en el backend
          horasData = await dispatch(obtenerHorasReporteTodosUsuariosConCliente(
            fechaInicioStr, 
            fechaFinStr, 
            clienteSeleccionado.value
          ));
        } else {
          // Usar endpoint sin filtro de cliente
          horasData = await dispatch(obtenerHorasReporteTodosUsuarios(fechaInicioStr, fechaFinStr));
        }
      }
      
      console.log('✅ Datos obtenidos del backend:', horasData?.length || 0, 'registros');
      setHorasConsultadas(horasData || []);
      
    } catch (error) {
      console.error('❌ Error al buscar horas:', error);
      alert('Error al buscar las horas: ' + (error.message || 'Error desconocido'));
      setHorasConsultadas([]);
    }
  };

  // Función para limpiar todos los filtros
  const limpiarFiltros = () => {
    setUsuarioSeleccionado(null);
    setClienteSeleccionado(null);
    setFechaInicio('');
    setFechaFin('');
    setMostrarFiltroCliente(false);
    setHorasConsultadas([]);
    dispatch(limpiarReporteUsuario());
  };

  // Preparar opciones para el select de usuarios
  const opcionesUsuarios = usuarios ? usuarios.map(usuario => ({
    value: usuario.id,
    label: `${usuario.nombre} ${usuario.apellidos}`
  })) : [];

  // Preparar opciones para el select de clientes
  const opcionesClientes = clientes ? clientes.map(cliente => ({
    value: cliente.id,
    label: cliente.nombre
  })) : [];

  // Estilos personalizados para React Select (paleta moderna oscura + verde)
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#0A100D',
      borderColor: state.isFocused ? 'rgba(40,199,111,0.6)' : 'rgba(255,255,255,0.10)',
      borderWidth: '0.5px',
      borderRadius: '10px',
      color: '#E8EFEA',
      minHeight: '42px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(40,199,111,0.20)' : 'none',
      '&:hover': {
        borderColor: 'rgba(40,199,111,0.45)'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#0C120F',
      border: '0.5px solid rgba(40,199,111,0.25)',
      borderRadius: '11px',
      overflow: 'hidden',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '6px',
      maxHeight: '230px',
      overflowY: 'auto',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'rgba(40,199,111,0.16)'
        : state.isFocused
        ? 'rgba(40,199,111,0.12)'
        : 'transparent',
      color: state.isSelected || state.isFocused ? '#EAF6EF' : '#CBD6CF',
      borderRadius: '8px',
      padding: '9px 11px',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#E8EFEA',
      fontSize: 13,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9FB0A6',
      fontSize: 13,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }),
    input: (provided) => ({
      ...provided,
      color: '#E8EFEA',
      fontSize: 13
    }),
    valueContainer: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      flexWrap: 'nowrap'
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? '#3FE07F' : '#5C6862',
      '&:hover': {
        color: '#3FE07F'
      }
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#9FB0A6',
      '&:hover': {
        color: '#E86D6D'
      }
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999
    })
  };

  // ===== Datos derivados para métricas y gráficos =====
  const totalHoras = useMemo(
    () => horasConsultadas.reduce((t, h) => t + parseFloat(h.horas || 0), 0),
    [horasConsultadas]
  );
  const registros = horasConsultadas.length;
  const usuariosUnicos = useMemo(
    () => new Set(horasConsultadas.map(h => (h.usuario ? `${h.usuario.nombre} ${h.usuario.apellidos}` : 'N/A'))).size,
    [horasConsultadas]
  );
  const diasUnicos = useMemo(
    () => new Set(horasConsultadas.map(h => h.fechaCarga)).size,
    [horasConsultadas]
  );
  const promedioDiario = diasUnicos ? totalHoras / diasUnicos : 0;

  // Barras: horas por usuario (top 8)
  const horasPorUsuario = useMemo(() => {
    const m = {};
    horasConsultadas.forEach(h => {
      const k = h.usuario ? `${h.usuario.nombre} ${h.usuario.apellidos}` : 'N/A';
      m[k] = (m[k] || 0) + parseFloat(h.horas || 0);
    });
    return Object.entries(m)
      .map(([nombre, horas]) => ({ nombre, horas: Math.round(horas * 100) / 100 }))
      .sort((a, b) => b.horas - a.horas)
      .slice(0, 8);
  }, [horasConsultadas]);

  // Dona: horas por cliente (top 4 + "Otros")
  const horasPorCliente = useMemo(() => {
    const m = {};
    horasConsultadas.forEach(h => {
      const k = h.cliente ? h.cliente.nombre : 'Sin cliente';
      m[k] = (m[k] || 0) + parseFloat(h.horas || 0);
    });
    const arr = Object.entries(m)
      .map(([nombre, horas]) => ({ nombre, horas: Math.round(horas * 100) / 100 }))
      .sort((a, b) => b.horas - a.horas);
    if (arr.length <= 5) return arr;
    const top = arr.slice(0, 4);
    const otros = arr.slice(4).reduce((t, x) => t + x.horas, 0);
    return [...top, { nombre: 'Otros', horas: Math.round(otros * 100) / 100 }];
  }, [horasConsultadas]);

  // Tooltip oscuro reutilizable para los gráficos
  const tooltipStyle = {
    background: '#0C120F',
    border: '0.5px solid rgba(40,199,111,0.3)',
    borderRadius: 10,
    color: '#E8EFEA',
    fontSize: 13,
  };

  // Abrevia "Nombre Apellido" a "Nombre A." para las etiquetas del eje X
  const abreviarNombre = (nombre) => {
    const partes = String(nombre).trim().split(' ');
    return partes.length > 1 ? `${partes[0]} ${partes[1][0]}.` : nombre;
  };

  return (
    <div className="reporte-usuario-container">
      <NavBarHome user={user} handleLogout={handleLogout} />
      
      <div className="reporte-usuario-main-content">
        <div className="reporte-usuario-header">
          <h1>Reporte por usuario</h1>
          <p>Consultá y analizá las horas trabajadas por usuario en un período.</p>
        </div>
            
        <div className="reporte-usuario-filtros-container">
          <div className="reporte-usuario-filtros-layout">
            <div className="reporte-usuario-form-group">
              <label>Usuario (opcional)</label>
              <Select
                styles={customSelectStyles}
                value={usuarioSeleccionado}
                onChange={setUsuarioSeleccionado}
                options={opcionesUsuarios}
                placeholder="Todos los usuarios"
                isClearable
                isSearchable
                className="reporte-usuario-select-usuario"
                classNamePrefix="rep-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="reporte-usuario-form-group">
              <label>Cliente (opcional)</label>
              <Select
                styles={customSelectStyles}
                value={clienteSeleccionado}
                onChange={setClienteSeleccionado}
                options={opcionesClientes}
                placeholder="Todos los clientes"
                isClearable
                isSearchable
                className="reporte-usuario-select-cliente"
                classNamePrefix="rep-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="reporte-usuario-form-group">
              <label>Fecha inicio</label>
              <div className="reporte-usuario-datepicker-wrapper">
                <DatePicker
                  selected={fechaInicio}
                  onChange={(date) => setFechaInicio(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecciona fecha inicio"
                  showPopperArrow={false}
                  autoComplete="off"
                  maxDate={new Date()}
                  popperPlacement="bottom-start"
                  popperModifiers={{
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: false,
                      boundariesElement: 'viewport'
                    }
                  }}
                  fixedHeight
                />
              </div>
            </div>

            <div className="reporte-usuario-form-group">
              <label>Fecha fin</label>
              <div className="reporte-usuario-datepicker-wrapper">
                <DatePicker
                  selected={fechaFin}
                  onChange={(date) => setFechaFin(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecciona fecha fin"
                  showPopperArrow={false}
                  autoComplete="off"
                  maxDate={new Date()}
                  minDate={fechaInicio}
                />
              </div>
            </div>

            <div className="reporte-usuario-botones-accion">
              <button
                className="reporte-usuario-btn-buscar"
                onClick={buscarHoras}
                disabled={!fechaInicio || !fechaFin || loading}
              >
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
              <button
                className="reporte-usuario-btn-limpiar"
                onClick={limpiarFiltros}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

      {/* Mostrar error si existe */}
      {error && (
        <div className="reporte-usuario-no-data">
          <p><strong>Error:</strong> {error}</p>
        </div>
      )}

      {/* Mostrar indicador de carga */}
      {loading && (
        <div className="reporte-usuario-loading">
          Cargando datos...
        </div>
      )}

      {/* Sin datos */}
      {!loading && horasConsultadas.length === 0 && (fechaInicio && fechaFin) && (
        <div className="reporte-usuario-no-data">
          <p>No se encontraron registros de horas para el período seleccionado.</p>
        </div>
      )}

      {/* Resultados */}
      {horasConsultadas.length > 0 && (
        <>
          {/* Tarjetas de métricas */}
          <div className="rep-metrics">
            <div className="rep-metric">
              <span className="rep-metric-lbl">Total de horas</span>
              <span className="rep-metric-val green">{Math.round(totalHoras * 100) / 100} h</span>
              <span className="rep-metric-sub">en el período</span>
            </div>
            <div className="rep-metric">
              <span className="rep-metric-lbl">Registros</span>
              <span className="rep-metric-val">{registros}</span>
              <span className="rep-metric-sub">cargas de horas</span>
            </div>
            <div className="rep-metric">
              <span className="rep-metric-lbl">Usuarios</span>
              <span className="rep-metric-val">{usuariosUnicos}</span>
              <span className="rep-metric-sub">con actividad</span>
            </div>
            <div className="rep-metric">
              <span className="rep-metric-lbl">Promedio diario</span>
              <span className="rep-metric-val">{promedioDiario.toFixed(1)} h</span>
              <span className="rep-metric-sub">por día con carga</span>
            </div>
          </div>

          {/* Gráficos */}
          <div className="rep-charts">
            <div className="rep-card">
              <p className="rep-chart-title">Horas por usuario</p>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={horasPorUsuario} margin={{ top: 24, right: 8, left: -8, bottom: 4 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="nombre" interval={0} tickFormatter={abreviarNombre} tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                  <YAxis tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip cursor={{ fill: 'rgba(40,199,111,0.08)' }} contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v) => [`${v} h`, 'Horas']} />
                  <Bar dataKey="horas" fill="#28C76F" radius={[6, 6, 0, 0]} maxBarSize={56}>
                    <LabelList dataKey="horas" position="top" fill="#3FE07F" fontSize={12} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rep-card">
              <p className="rep-chart-title">Distribución por cliente</p>
              <div className="rep-donut-row">
                <div className="rep-donut-chart">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={horasPorCliente} dataKey="horas" nameKey="nombre" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="none">
                        {horasPorCliente.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v, n) => [`${v} h`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="rep-legend">
                  {horasPorCliente.map((e, i) => (
                    <div className="rep-legend-li" key={i}>
                      <span className="rep-legend-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></span>
                      <span className="rep-legend-name">{e.nombre}</span>
                      <span className="rep-legend-pct">{totalHoras ? Math.round((e.horas / totalHoras) * 100) : 0}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de detalle */}
          <div className="reporte-usuario-table-container">
            <div className="reporte-usuario-tabla-head">
              <p className="rep-chart-title" style={{ margin: 0 }}>Detalle de registros</p>
              <button
                className="reporte-usuario-btn-exportar"
                onClick={exportarAExcel}
                disabled={horasConsultadas.length === 0}
              >
                Exportar a Excel
              </button>
            </div>

            <table className="reporte-usuario-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Seniority</th>
                  <th>Cliente</th>
                  <th>Servicio</th>
                  <th>Tarea</th>
                  <th>Descripción</th>
                  <th>Horas</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {horasConsultadas.map((hora, index) => (
                  <tr key={index}>
                    <td>{hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'N/A'}</td>
                    <td>{hora.senorityhora ? <span className="rep-pill">{hora.senorityhora}</span> : 'N/A'}</td>
                    <td>{hora.cliente ? hora.cliente.nombre : 'Sin cliente'}</td>
                    <td>{hora.servicio ? hora.servicio.nombre : 'Sin servicio'}</td>
                    <td>{hora.tarea ? hora.tarea.nombre : 'Sin tarea'}</td>
                    <td>{hora.descripcion || 'Sin descripción'}</td>
                    <td className="reporte-usuario-horas-cell">{formatearHoras(hora.horas)}</td>
                    <td>{formatearFecha(hora.fechaCarga)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="6">Total</td>
                  <td className="reporte-usuario-horas-cell">{formatearHoras(Math.round(totalHoras * 100) / 100)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default ReporteUsuario;